import { useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useLogto } from '@logto/react'
import { toast } from 'sonner'
import { API_BASE_URL, API_RESOURCE } from '@/auth/LogtoConfig'
import { applyEventToCache, type ActivityEvent } from './applyEventToCache'
import { notificationKeys } from '../notifications/useNotifications'
import type { NotificationPayload } from '../notifications/types'

/**
 * Subscribes to the workspace SSE stream and keeps React Query caches
 * up-to-date in real time.
 *
 * Call once per workspace (e.g. in WorkspaceProvider).
 */
export function useWorkspaceEvents(
  workspaceSlug: string | undefined,
  currentPersonId?: string | null,
) {
  const qc = useQueryClient()
  const { getAccessToken } = useLogto()
  const lastEventIdRef = useRef<number | null>(null)
  const reconnectAttemptsRef = useRef(0)

  useEffect(() => {
    if (!workspaceSlug) return

    const slug = workspaceSlug
    let eventSource: EventSource | null = null
    let cancelled = false
    let reconnectTimer: number | null = null

    async function connect() {
      if (cancelled) return

      let token: string | undefined
      try {
        token = await getAccessToken(API_RESOURCE)
      } catch {
        // Auth not ready — retry after a short delay
        if (!cancelled) {
          reconnectTimer = window.setTimeout(connect, 2000)
        }
        return
      }

      if (!token || cancelled) return

      const url = new URL(`${API_BASE_URL}/api/v1/workspaces/${slug}/events`)
      url.searchParams.set('access_token', token)
      const lastId = lastEventIdRef.current
      if (lastId !== null) url.searchParams.set('last_event_id', String(lastId))

      eventSource = new EventSource(url.toString())

      // Initial cursor — server tells us current max event id and confirms connection
      eventSource.addEventListener('cursor', (e: MessageEvent) => {
        try {
          const data = JSON.parse(e.data) as { max_event_id?: unknown }
          if (typeof data.max_event_id === 'number') {
            lastEventIdRef.current = data.max_event_id
          }
          reconnectAttemptsRef.current = 0
        } catch {
          // ignore
        }
      })

      const handleRecord = (e: MessageEvent) => {
        try {
          const event: ActivityEvent = JSON.parse(e.data)
          lastEventIdRef.current = event.id
          applyEventToCache(qc, slug, event)
        } catch (err) {
          console.warn('[SSE] failed to parse event:', err)
        }
      }

      eventSource.addEventListener('record.created', handleRecord)
      eventSource.addEventListener('record.updated', handleRecord)
      eventSource.addEventListener('record.archived', handleRecord)
      eventSource.addEventListener('edge.created', handleRecord)
      eventSource.addEventListener('edge.deleted', handleRecord)

      // Handle per-user notifications
      eventSource.addEventListener('notification.created', (e: MessageEvent) => {
        try {
          const event: ActivityEvent = JSON.parse(e.data)
          lastEventIdRef.current = event.id
          const payload = event.payload as NotificationPayload & {
            recipient_person_id?: string
            action_type?: string
          }

          // Only act on notifications addressed to the current user
          if (!currentPersonId || payload.recipient_person_id !== currentPersonId) return

          // Invalidate notifications cache so inbox updates
          qc.invalidateQueries({ queryKey: notificationKeys.all(slug) })

          // Show a toast
          const message = buildNotificationMessage(payload)
          if (message) {
            toast(message, { duration: 5000 })
          }
        } catch (err) {
          console.warn('[SSE] failed to parse notification event:', err)
        }
      })

      eventSource.onerror = () => {
        if (eventSource?.readyState === EventSource.CLOSED) {
          eventSource.close()
          eventSource = null
          if (cancelled) return
          const attempt = reconnectAttemptsRef.current++
          const delay = Math.min(30_000, 1000 * Math.pow(2, attempt))
          reconnectTimer = window.setTimeout(connect, delay)
        }
      }
    }

    connect()

    return () => {
      cancelled = true
      if (reconnectTimer !== null) window.clearTimeout(reconnectTimer)
      eventSource?.close()
      eventSource = null
    }
  }, [workspaceSlug, currentPersonId, getAccessToken, qc])
}

function buildNotificationMessage(payload: NotificationPayload & { action_type?: string }): string | null {
  const actor = payload.actor_name ?? 'Кто-то'
  const title = payload.task_title ?? payload.record_title ?? ''

  switch (payload.action_type) {
    case 'task.assigned':
      return title ? `${actor} назначил вам задачу «${title}»` : `${actor} назначил вам задачу`
    case 'task.commented':
      return title ? `${actor} прокомментировал задачу «${title}»` : `${actor} оставил комментарий`
    case 'task.status_changed':
      return title && payload.new_status
        ? `${actor} изменил статус «${title}» → ${payload.new_status}`
        : `${actor} изменил статус задачи`
    case 'comment.replied':
      return title ? `${actor} ответил на ваш комментарий в «${title}»` : `${actor} ответил на ваш комментарий`
    default:
      return null
  }
}
